import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projects, pickLang } from '../mock';
import PortfolioBackground from '../components/PortfolioBackground';
import ShowreelOverlay from '../components/ui/ShowreelOverlay';

// Vidéo servie depuis /public/videos (pas bundlée par webpack) pour un chargement plus rapide.
// Un seul showreel désormais (avant : un par catégorie de filtre).
const showreelVideo = '/videos/showreel_2026.mp4';

export const Portfolio = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const categoryLabels = {
    Ads: t('portfolio.filter.ads'),
    Fiction: t('portfolio.filter.fiction'),
    Animation: t('portfolio.filter.animation'),
  };
  const [filter, setFilter] = useState('all');
  const [showShowreel, setShowShowreel] = useState(false);
  const [savedScrollY, setSavedScrollY] = useState(0);

  // Scroll en haut de la page quand le composant est monté
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Blocage du scroll (natif + molette/tactile) pendant que le showreel est ouvert
  useEffect(() => {
    if (!showShowreel) return;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    const preventDefault = (e) => e.preventDefault();
    window.addEventListener('wheel', preventDefault, { passive: false });
    window.addEventListener('touchmove', preventDefault, { passive: false });

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.removeEventListener('wheel', preventDefault);
      window.removeEventListener('touchmove', preventDefault);

      window.scrollTo({ top: savedScrollY, behavior: 'instant' }); // restore exact position
    };
  }, [showShowreel, savedScrollY]);



  
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fadeIn');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-element');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [filter]);

 const filteredProjects =
  filter === 'all'
    ? projects
    : projects.filter(p => p.meta.category.toLowerCase() === filter);


const categories = [
  { id: 'all', label: t('portfolio.filter.all') },
  { id: 'ads', label: t('portfolio.filter.ads') },
  { id: 'fiction', label: t('portfolio.filter.fiction') },
  { id: 'animation', label: t('portfolio.filter.animation') }
];


  return (
    <div className="min-h-screen bg-ink-950 bg-textured pt-24 pb-20 relative">

      <PortfolioBackground />

      <div className="container mx-auto px-6 relative z-10">
      {/* Header */}

<div className="text-center mb-8 fade-element relative z-20">
  <div className="eyebrow mb-6 justify-center">{t('portfolio.eyebrow')}</div>
  <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto">
    {t('portfolio.page_subtitle')}
  </p>
</div>


{/* Bouton Play */}
<div className="flex justify-center mb-10 fade-element relative z-20">
  <button
    onClick={() => {
      setSavedScrollY(window.scrollY);
      setShowShowreel(true);
    }}
    className="btn-primary"
  >
    {t('portfolio.showreel_button')}
  </button>
</div>

<ShowreelOverlay
  videoSrc={showreelVideo}
  playFull={showShowreel}
  onClose={() => setShowShowreel(false)}
/>











{/* Filter Tabs */}
<div
  className="flex flex-wrap justify-center gap-3 mb-12 fade-element"
  style={{ transitionDelay: '0.15s' }}
>
  {categories.map(category => (
    <button
      key={category.id}
      onClick={() => setFilter(category.id)}
      className={`font-mono px-5 py-2.5 rounded-sm uppercase tracking-[0.12em] text-xs transition-all duration-300 border ${
        filter === category.id
          ? 'bg-ember text-ink-950 border-ember font-bold'
          : 'bg-transparent text-gray-400 border-white/15 hover:border-ember/60 hover:text-ember'
      }`}
    >
      {category.label}
    </button>
  ))}
</div>



        {/* Projects Grid — vraie grille avec espacement (gap) et bordure
            complète sur chaque carte plutôt qu'un fond de grille dans les
            interstices : l'ancienne version (gap-px + bg sur le conteneur)
            laissait un bandeau vide coloré à la place des cartes manquantes
            quand une catégorie filtrée ne remplissait pas une rangée
            complète (ex: "Fiction", 1 seul projet). Sans carte, il n'y a
            désormais plus rien d'affiché. */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredProjects.map((project, index) => (
            <Link
              key={project.id}
              to={`/project/${project.id}`}
              className="project-card group fade-element border border-white/10"
              style={{ transitionDelay: `${(index % 6) * 0.1}s` }}
            >
              <img
                src={project.thumbnail}
                alt={pickLang(project.title, lang)}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent"></div>
              <div className="holo-sheen"></div>
              <div className="project-panel">
                <div className="grain"></div>
                <div className="meta text-amber font-mono text-[11px] tracking-widest uppercase mb-1">
                  {categoryLabels[project.meta?.category] || project.meta?.category} — {project.meta?.year}
                </div>
                <h3 className="font-display text-2xl uppercase">{pickLang(project.title, lang)}</h3>
              </div>
            </Link>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20 fade-element">
            <p className="font-body text-gray-400 text-lg">
              {t('portfolio.no_projects')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
